import React, { FC, useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Trans } from 'react-i18next';
import Delete from '../../assets/delete.png';
import Download from '../../assets/download_signature.png';
import { AddSignatureInputProps } from './addSignature.type';
import { getTrimmedSignature, getIdAttributes } from '../../utils/helper';

export const idPrefix: string = 'add-signature';
const AddSignatureInput: FC<AddSignatureInputProps> = (props) => {
  const { width = '100%', height = '190px' } = props;

  const prefix: string = props.idPrefix || idPrefix;

  const [isDrawing, setIsDrawing] = useState(false);
  const [isSigned, setIsSigned] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const sigCanvas = useRef<SignatureCanvas>(null);

  const containerStyle = {
    maxWidth: width,
    height,
  };

  useEffect(() => {
    if (props.initialValue) {
      setImagePreview(props.initialValue);
    }
  }, [props.initialValue]);

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file && file.type.startsWith('image/')) {
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      setIsDrawing(false);

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        props.onChange(base64);
      };

      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
      props.onChange('');
    }
  };

  const handleDelete = () => {
    setImagePreview(null);
    setIsDrawing(false);
    setIsSigned(false);
    props.onChange('');
    sigCanvas.current?.clear();
  };

  const handleSignatureDrawingClick = () => {
    setIsDrawing(true);
    setImagePreview(null);
    props.onChange('');
  };

  const handleSignEnd = () => {
    setIsSigned(true);
    const canvas = sigCanvas.current;
    if (canvas) {
      const base64 = getTrimmedSignature({ current: sigCanvas.current });
      props.onChange(base64);
    }
  };

  const handleFileInputClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div {...getIdAttributes(prefix, 'container')}>
      {!isDrawing ? (
        <>
          {imagePreview ? (
            <div
              className="relative w-full border border-dashed border-[#A3A3A3] rounded-[20px] bg-white flex items-center justify-center"
              style={containerStyle}
              {...getIdAttributes(prefix, 'image-preview-container')}
            >
              <img
                src={Delete}
                alt="Delete"
                onClick={handleDelete}
                className="w-5 h-5 absolute top-3 right-3 cursor-pointer z-10"
                {...getIdAttributes(prefix, 'delete-button')}
              />
              <img
                src={imagePreview}
                alt="Uploaded preview"
                className="max-h-[90%] max-w-[90%] rounded"
                {...getIdAttributes(prefix, 'preview-image')}
              />
            </div>
          ) : (
            <button
              onClick={handleSignatureDrawingClick}
              className="relative w-full border  border-dashed border-[#D8D8D8] rounded-[20px] flex items-center justify-center bg-white"
              style={containerStyle}
              {...getIdAttributes(prefix, 'draw-signature-button')}
            >
              <div className="text-center">
                <p className="text-[#A3A3A3] text-[16px] mt-6">
                  <Trans i18nKey="onboarding.drawByHand" />{' '}
                  <Trans i18nKey="onboarding.uploadSignature" />
                </p>

                <input
                  type="file"
                  accept="image/*"
                  onChange={handleUpload}
                  onClick={(e) => e.stopPropagation()}
                  className="hidden"
                  ref={fileInputRef}
                  {...getIdAttributes(prefix, 'file-input')}
                />

                <div className="cursor-pointer flex flex-col items-center mt-5">
                  <img
                    src={Download}
                    alt="Upload Icon"
                    className="w-12 h-11"
                    onClick={handleFileInputClick}
                    {...getIdAttributes(prefix, 'upload-icon')}
                  />
                  <div
                    className="w-32 py-1.5 rounded-full bg-white border text-[#1A1A1A] text-sm border-[#D8D8D8] mt-5 text-center mb-5"
                    onClick={handleFileInputClick}
                    {...getIdAttributes(prefix, 'browse-button')}
                  >
                    <Trans i18nKey="onboarding.browseFile" />
                  </div>
                </div>
              </div>
            </button>
          )}
        </>
      ) : (
        <div
          className="relative w-full border border-dashed border-[#D8D8D8] rounded-[20px] flex items-center justify-center bg-white"
          style={containerStyle}
          {...getIdAttributes(prefix, 'signature-canvas-container')}
        >
          {isSigned && (
            <button
              onClick={handleDelete}
              className="absolute top-3 right-3 z-10 cursor-pointer"
              {...getIdAttributes(prefix, 'canvas-delete-button')}
            >
              <img
                src={Delete}
                alt="Delete"
                className="w-5 h-5 text-sm text-red-500"
              />
            </button>
          )}

          <SignatureCanvas
            ref={sigCanvas}
            penColor="black"
            onEnd={handleSignEnd}
            canvasProps={{
              className: 'rounded-md w-full border-[#707070] rounded-[10px]',
              style: { height: '100%' },
              ...getIdAttributes(prefix, 'signature-canvas'),
            }}
          />
        </div>
      )}
    </div>
  );
};

export default AddSignatureInput;
